import React, { useState, useRef, useEffect } from 'react';
import { Mic, Circle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/contexts/AuthContext';
import { messagePersistence } from '@/utils/messagePersistence';

interface VoiceRecorderProps {
  onTranscriptionComplete: (text: string) => void;
  className?: string;
  onRecordingStateChange?: (isRecording: boolean, timeRemaining: number) => void;
  stopRecording?: (stopFn: () => void) => void;
  userId?: string;
  isProcessing?: boolean;
  onProcessingStateChange?: (isProcessing: boolean) => void;
}

const MAX_RECORDING_TIME = 300; // 5 minutes in seconds

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({ 
  onTranscriptionComplete,
  className,
  onRecordingStateChange,
  stopRecording,
  userId,
  isProcessing = false,
  onProcessingStateChange
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(MAX_RECORDING_TIME);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { user } = useAuth();

  useEffect(() => {
    if (isRecording) {
      startTimeRef.current = Date.now();
      timerRef.current = setInterval(() => {
        if (startTimeRef.current) {
          const elapsed = Math.floor((Date.now() - startTimeRef.current) / 1000);
          const remaining = MAX_RECORDING_TIME - elapsed;
          
          if (remaining <= 0) {
            handleStopAndProcess();
            setTimeRemaining(0);
          } else {
            setTimeRemaining(remaining);
          }
        }
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setTimeRemaining(MAX_RECORDING_TIME);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [isRecording]);

  useEffect(() => {
    onRecordingStateChange?.(isRecording, timeRemaining);
  }, [isRecording, timeRemaining, onRecordingStateChange]);

  const getSupportedMimeType = () => {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/ogg;codecs=opus',
      'audio/ogg'
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return null;
  };

  const processAudio = async (audioBlob: Blob) => {
    // Generate a unique ID for this voice message
    const voiceMessageId = `voice_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    try {
      if (audioBlob.size === 0) {
        throw new Error('No audio was recorded. Please try recording again.');
      }
      
      // Store audio blob before processing for retry capability
      messagePersistence.storeVoiceBlob(voiceMessageId, audioBlob);
      
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const base64 = (reader.result as string).split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
      });
      reader.readAsDataURL(audioBlob);
      
      const base64Audio = await base64Promise;

      if (!user) {
        throw new Error('Please log in to use voice recording');
      }

      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) {
        throw new Error('Session expired - please log in again');
      }

      let data, error;

      // Try Fast Voice-to-Text (Distil-Whisper) first (faster: ~200-500ms)
      try {
        const fastResponse = await supabase.functions.invoke('fast-voice-to-text', {
          headers: {
            Authorization: `Bearer ${session.session.access_token}`,
          },
          body: { 
            audio: base64Audio,
            userId: user.id
          }
        });
        
        if (!fastResponse.error && fastResponse.data?.text) {
          data = fastResponse.data;
          error = null;
        } else {
          throw new Error('Fast voice-to-text failed: ' + (fastResponse.error?.message || 'No text returned'));
        }
      } catch (fastError) {
        
        // Fallback to original system (slower: ~1600ms)
        const endpoint = isMobile ? 'mobile-voice-to-text' : 'voice-to-text';
        
        const fallbackResponse = await supabase.functions.invoke(endpoint, {
          headers: {
            Authorization: `Bearer ${session.session.access_token}`,
          },
          body: { 
            audio: base64Audio,
            userId: user.id
          }
        });
        
        data = fallbackResponse.data;
        error = fallbackResponse.error;
      }

      if (error) {
        throw new Error('Failed to process voice recording');
      }

      if (data && data.text) {
        onTranscriptionComplete(data.text);
        // Clean up voice blob on successful transcription
        messagePersistence.removeVoiceBlob(voiceMessageId);
        // Signal that processing has completed successfully
        onProcessingStateChange?.(false);
      } else {
        throw new Error('No transcription received from the service');
      }
    } catch (error) {
      
      let errorMessage = 'Failed to process voice recording';
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      // Keep voice blob for potential retry - don't remove it on error
      
      toast({
        title: "Processing Error",
        description: errorMessage,
        variant: "destructive",
      });
      onTranscriptionComplete("");
      // Signal that processing has ended due to error
      onProcessingStateChange?.(false);
    }
  };

  const handleStopAndProcess = async () => {
    if (mediaRecorderRef.current && isRecording) {
      try {
        mediaRecorderRef.current.stop();
        setIsRecording(false);
      } catch (error) {
        setIsRecording(false);
        toast({
          title: "Recording Error",
          description: "Failed to stop recording properly. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        },
        video: false
      });

      const mimeType = getSupportedMimeType();
      if (!mimeType) {
        throw new Error('No supported audio MIME type found');
      }

      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: mimeType
      });

      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorderRef.current.onerror = (event) => {
        toast({
          title: "Recording Error", 
          description: "An error occurred while recording. Please try again.",
          variant: "destructive",
        });
      };

      mediaRecorderRef.current.onstop = async () => {
        try {
          // Signal that processing has started
          onProcessingStateChange?.(true);
          const audioBlob = new Blob(chunksRef.current, { type: mimeType });
          await processAudio(audioBlob);
        } catch (error) {
          toast({
            title: "Processing Error",
            description: "Failed to process the recording. Please try again.",
            variant: "destructive",
          });
          onTranscriptionComplete("");
          // Signal that processing has ended due to error
          onProcessingStateChange?.(false);
        } finally {
          // Clean up the stream
          stream.getTracks().forEach(track => track.stop());
        }
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setTimeRemaining(MAX_RECORDING_TIME);

    } catch (error) {
      let errorMessage = "Could not access microphone. ";
      
      if (error instanceof Error) {
        if (error.name === 'NotAllowedError') {
          errorMessage += "Please grant microphone permissions.";
        } else if (error.name === 'NotFoundError') {
          errorMessage += "No microphone found.";
        } else if (error.name === 'NotReadableError') {
          errorMessage += "Microphone is already in use.";
        } else {
          errorMessage += error.message;
        }
      }
      
      toast({
        title: "Microphone Error",
        description: errorMessage,
        variant: "destructive",
      });
      
      setIsRecording(false);
    }
  };

  const handleRecordButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    
    if (isRecording) {
      handleStopAndProcess();
    } else {
      startRecording();
    }
  };

  useEffect(() => {
    if (stopRecording) {
      stopRecording(handleStopAndProcess);
    }
  }, [stopRecording]);

  return (
    <div className={cn("flex items-center gap-1", className)}>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRecordButtonClick}
              disabled={isProcessing}
              className="h-8 w-8 p-0 rounded-lg hover:bg-accent"
              aria-label={isRecording ? 'Stop and transcribe' : 'Speech to text'}
              title={isRecording ? 'Stop and transcribe' : 'Speech to text'}
            >
              {isProcessing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : isRecording ? (
                <Circle className="h-4 w-4 fill-current text-red-500 animate-pulse" />
              ) : (
                <Mic className="h-4 w-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">{isRecording ? 'Stop and transcribe' : 'Speech to text'}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
};

export default VoiceRecorder;
